import React, { useEffect, useState } from "react";

import { useSelector, useDispatch } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { useWeb3React } from "@web3-react/core";

import { Button, Card, CardBody, Spinner, Label } from "reactstrap";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { styled } from "@mui/system";
import bn from "bignumber.js";

import Avatar from "../Avatar";
import AssetsList from "./AssetsList";
import TokensModal from "./TokensModal";

import { chainIds } from "../../constants";
import {
	useTokenStatus,
	useContractAction,
	useERC20Holders,
} from "../../hooks";
import CopyClipboard from "../common/CopyClipboard";
import ActionModal from "../common/ActionModal";
import {
	addContractCustomImage,
	transContract,
} from "../../store/actions/contract";
import EditPopup from "../common/EditPopup";
import { ethers } from "ethers";
import { ethersToBN, getContractAbi } from "../../utils";
import { useCallback } from "react";

export default function AssetsContractInfo({ contractId }) {
	const { account, library } = useWeb3React();
	const dispatch = useDispatch();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);

	const { getTokensTotal } = useTokenStatus();
	const {
		transferContract,
		transferERC20,
		getContractOwner,
		mintToken,
		show,
		stage,
		error,
		action,
	} = useContractAction();

	const contracts = useSelector((state) => state.contract.items);
	const allTokens = useSelector((state) => state.token.items);

	const [openActionModal, setOpenActionModal] = useState(false);
	const handleOpenActionModal = () => setOpenActionModal(true);
	const [actionModalType, setActionModalType] = useState(undefined);
	const handleCloseActionModal = () => {
		setActionModalType(undefined);
		setOpenActionModal(false);
	};
	const [erc20TokensMaxSupply, setErc20TokensMaxSupply] = useState("");
	const [erc20TokensTotalSupply, setErc20TokensTotalSupply] = useState("");
	const [erc20Balance, setErc20Balance] = useState("");

	const [nftTotalSupply, setNftTotalSupply] = useState("");
	const [nftMaxSupply, setNftMaxSupply] = useState("");

	const [owner, setOwner] = useState(null);
	const [tokensAmount, setTokensAmount] = useState(null);
	const [contractUseddGas, setContractUseddGas] = useState(0);
	const [tokentUsedGas, setTokenUsedGas] = useState(0);

	const contract = contracts.find((item) => item._id === contractId);
	const tokens = allTokens.filter((item) => item.contractId === contract._id);
	const usedMC = useSelector((state) =>
		state.contract.templates.find(
			(template) => template._id === contract?.masterContract
		)
	);

	const { balancesWithHolders, fetchHoldersBalances, totalMintGasUsed } =
		useERC20Holders(contract?.type === "ERC20" ? contract.address : null);

	const updateERC20Stuff = useCallback(() => {
		if (contract?.type === "ERC20") {
			const contract_ = new ethers.Contract(
				contract.address,
				getContractAbi("ERC20"),
				!!library ? library.getSigner() : library
			);

			if (!!account) {
				contract_.balanceOf(account).then((balanceBN) => {
					const decimals = new bn(10).pow(18);
					const balanceFormatted = ethersToBN(balanceBN)
						.div(decimals)
						.toNumber()
						.toFixed();

					setErc20Balance(balanceFormatted);
				});
			}

			contract_.MAX_SUPPLY().then((maxSupply) => {
				const decimals = new bn(10).pow(18);
				const maxSupplyFormatted = ethersToBN(maxSupply)
					.div(decimals)
					.toNumber()
					.toFixed(0);

				setErc20TokensMaxSupply(maxSupplyFormatted);
			});

			contract_.totalSupply().then((totalSupplyBN) => {
				const decimals = new bn(10).pow(18);
				const totalSupplyFormatted = ethersToBN(totalSupplyBN)
					.div(decimals)
					.toNumber()
					.toFixed(0);

				setErc20TokensTotalSupply(totalSupplyFormatted);
			});
		}
	}, [account, contract, library]);

	const updateNftStuff = useCallback(() => {
		if (
			["ERC721", "ERC721A", "ERC721PSI", "ERC1155"].includes(contract?.type)
		) {
			const contract_ = new ethers.Contract(
				contract.address,
				getContractAbi(contract.type),
				!!library ? library.getSigner() : library
			);

			if (contract_["totalSupply()"]) {
				contract_.totalSupply().then((totalSupplyBN) => {
					const totalSupplyFormatted = ethersToBN(totalSupplyBN).toFixed();

					setNftTotalSupply(totalSupplyFormatted);
				});
			}

			if (contract_["MAX_SUPPLY()"]) {
				contract_.MAX_SUPPLY().then((maxSupplyBN) => {
					const maxSupplyFormatted = ethersToBN(maxSupplyBN).toFixed();

					setNftMaxSupply(maxSupplyFormatted);
				});
			}

			if (contract_["getNextTokenId()"]) {
				contract_.getNextTokenId().then((nextTokenIdBN) => {
					const totalSupplyFormatted = ethersToBN(nextTokenIdBN)
						.minus(1)
						.toFixed();

					setNftTotalSupply(totalSupplyFormatted);
				});
			}
		}
	}, [contract, library]);

	useEffect(() => {
		updateERC20Stuff();
	}, [updateERC20Stuff]);

	useEffect(() => {
		updateNftStuff();
	}, [updateNftStuff]);

	useEffect(() => {
		if (contract) {
			getContractOwner(contractId).then((res) => setOwner(res.owner));
			setTokenUsedGas(0);
			setContractUseddGas(contract.gas);
			getTokensTotal(contract._id).then((res) => {
				console.log(res);
				setTokensAmount(res);
				setTokenUsedGas(res.totalGasMint?.toFixed(8));
			});
		}
		return () => {
			setContractUseddGas(0);
			setTokenUsedGas(0);
			setTokensAmount(null);
			setOwner(null);
		};
	}, [dispatch, contract, contractId, getTokensTotal, getContractOwner]);

	const renderUserImg = () => {
		if (contract !== null && contract.image) {
			return (
				<img
					height="110"
					width="110"
					alt="user-avatar"
					src={
						contract.image.includes("https://")
							? contract.image
							: `${process.env.REACT_APP_IPFS_HOST}/${
									contract?.customImage ?? contract.image
							  }`
					}
					className="img-fluid rounded mt-3 mb-2"
				/>
			);
		} else {
			const stateNum = Math.floor(Math.random() * 6),
				states = [
					"light-success",
					"light-danger",
					"light-warning",
					"light-info",
					"light-primary",
					"light-secondary",
				],
				color = states[stateNum];
			return (
				<Avatar
					initials
					color={color}
					className="rounded mt-3 mb-2"
					content={contract?.customName ?? contract.name}
					contentStyles={{
						borderRadius: 6,
						fontSize: "calc(48px)",
						width: "100%",
						height: "100%",
					}}
					style={{
						height: "110px",
						width: "110px",
					}}
				/>
			);
		}
	};

	if (!contract) {
		return null;
	}
	const actiopProps = () => {
		switch (actionModalType) {
			case "mint":
				return {
					header: "Mint tokens",
					text: "Enter the wallet address of the new owner.",
					type: contract.type,
					count: "",
					modalType: "mint-token",
				};
			case "transfer":
				return contract.type === "ERC20"
					? {
							header: "Transfer token",
							text: "Enter the wallet address of the new owner.",
							type: contract.type,
							count: "",
							modalType: "transfer-token",
					  }
					: {
							header: "Transfer contract",
							text: "Enter the wallet address of the new owner.",
							type: contract.type,
							count: "",
							modalType: "transfer-contract",
					  };
			case "delete":
				return {
					header: "Delete contract",
					text: "By choosing to SUBMIT, you confirm your consent to the transfer of this contract to the zero address. This action cannot be undone.",
					type: contract.type,
					count: "",
					modalType: "delete-contract",
				};
			default:
				return {
					header: "Transfer contract",
					text: "Enter the wallet address of the new owner.",
					type: contract.type,
					count: "",
					modalType: "transfer-contract",
				};
		}
	};

	const handleSubmit = async (data, actionType) => {
		const { address, count } = data;

		if (actionType === "mint-token") {
			if (contract.type === "ERC20") {
				const decimals = new bn(10).pow(18);
				const amount = new bn(count).times(decimals).toFixed();

				await mintToken(address, contractId, amount).then((res) => {
					const { gasUsed } = res;

					fetchHoldersBalances();
					updateERC20Stuff();
					updateNftStuff();
					if (contract) {
						getContractOwner(contractId).then((res) => setOwner(res.owner));
						setTokenUsedGas(gasUsed);
						setContractUseddGas(contract.gas);
						getTokensTotal(contract._id).then((res) => {
							setTokensAmount(res);
							setTokenUsedGas(res.totalGasMint?.toFixed(8));
						});
					}
				});
			}
		}

		if (actionType === "transfer-token") {
			const decimals = new bn(10).pow(18);
			const amount = new bn(count).times(decimals).toFixed();

			await transferERC20(address, contractId, amount).then(() => {
				fetchHoldersBalances();
				updateERC20Stuff();
			});
		}

		if (
			actionType === "transfer-contract" ||
			actionType === "delete-contract"
		) {
			transferContract(contractId, address)
				.then((res) => {
					dispatch(transContract(contractId, res.gasUsed));
				})
				.then(() => history.replace(`/contracts/${contract._id}`))
				.catch((error) => console.log(error.message));
		}
		if (actionType === "delete-contract") {
			transferContract(contractId, address)
				.then((res) => {
					dispatch(transContract(contractId, res.gasUsed));
				})
				.then(() => history.replace(`/contracts/${contract._id}`))
				.catch((error) => console.log(error.message));
		}

		handleCloseActionModal();
	};

	const handleMintContract = () => {
		setActionModalType("mint");
		handleOpenActionModal();
	};

	const handleTransferContract = () => {
		setActionModalType("transfer");
		handleOpenActionModal();
	};
	const handleBurntContract = () => {
		setActionModalType("delete");
		handleOpenActionModal();
	};

	const handleChangeText = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const ImageCollection = styled("input")({
		display: "none",
	});
	const handleChangeImage = (event) => {
		let file = event.target.files[0];
		dispatch(addContractCustomImage(contractId, file));
	};

	return (
		<>
			{actionModalType && (
				<ActionModal
					actionProps={actiopProps()}
					open={openActionModal}
					handleClose={handleCloseActionModal}
					handleSubmit={handleSubmit}
				/>
			)}
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					flexBasis: "75%",
				}}
			>
				<Box sx={{ minHeight: "78vh", margin: "0 auto", width: "100%" }}>
					<Card className="mb-3">
						<CardBody
							style={{
								paddingLeft: "70px",
								paddingRight: "30px",
							}}
						>
							<div className="d-flex">
								<div
									className="user-avatar-section me-5"
									style={{
										minHeight: "110px",
										minWidth: "110px",
									}}
								>
									<Box
										className="d-flex align-items-center flex-column position-relative"
										sx={{
											"&:hover svg": { visibility: "visible" },
										}}
									>
										<ImageCollection
											id="custom-image"
											accept="image/png, image/jpeg, image/gif"
											type="file"
											onChange={handleChangeImage}
										/>
										<Label for="custom-image">
											<EditOutlinedIcon
												id="edit-description"
												sx={{
													visibility: "hidden",
													position: "absolute",
													bottom: "-5px",
													right: "-12px",
													cursor: "pointer",
												}}
											/>
										</Label>

										{renderUserImg()}
									</Box>
								</div>
								<div className="info-container" style={{ width: "100%" }}>
									<div className="border-bottom pb-50 mb-1 d-flex justify-content-between">
										<h4 className="fw-bolder ">Details</h4>
										{owner === account && (
											<>
												{["ERC20"].includes(contract?.type) ? (
													<Button
														className="px-5"
														color="primary"
														onClick={handleMintContract}
													>
														Mint
													</Button>
												) : (
													<Link to={`mint/${contract._id}`}>
														<Button className="px-5" color="primary">
															Mint
														</Button>
													</Link>
												)}
												<Button
													className="px-5 me-3"
													color="primary"
													onClick={handleTransferContract}
												>
													Transfer
												</Button>
												<Button
													className="px-5 me-3"
													color="primary"
													onClick={handleBurntContract}
												>
													Del/Burnt
												</Button>
											</>
										)}
									</div>
									<table className="mt-2 mt-xl-0 w-100">
										<tbody>
											<tr className="table-row">
												<th>Name</th>
												<td>
													{contract?.customName ?? contract.name}
													<EditOutlinedIcon
														id="edit-name"
														sx={{
															ml: "1rem",
															cursor: "pointer",
														}}
														onClick={(event) => handleChangeText(event, "name")}
													/>
												</td>
											</tr>
											<tr className="table-row">
												<th>Symbol</th>
												<td>{contract.symbol || "No info"}</td>
											</tr>
											{owner !== account && (
												<tr className="table-row">
													<th>Owner</th>
													<td>
														{owner ===
														"0x0000000000000000000000000000000000000001"
															? "Contract delete"
															: owner}
														{owner !==
															"0x0000000000000000000000000000000000000001" && (
															<CopyClipboard target={owner} />
														)}
													</td>
												</tr>
											)}
											<tr className="table-row">
												<th>Address</th>
												<td>
													{contract.address || "No info"}
													<CopyClipboard target={contract.address} />
												</td>
											</tr>
											<tr className="table-row">
												<th>Contract Link</th>
												<td>
													{(
														<a
															href={`${chainIds[contract.chain].link}${
																contract.address
															}`}
															rel="noreferrer"
															target="_blank"
														>
															link to Etherscan
														</a>
													) || "No info"}
												</td>
											</tr>
											{contract?.type !== "ERC20" && (
												<tr className="table-row">
													<th>IPFS Link</th>
													<td>
														{(
															<a
																href={`${process.env.REACT_APP_IPFS_HOST}/${contract.meta}`}
																rel="noreferrer"
																target="_blank"
															>
																link
															</a>
														) || "No info"}
													</td>
												</tr>
											)}
											<tr className="table-row">
												<th>Description</th>
												<td>
													{contract?.customDescription ?? contract.description}
													<EditOutlinedIcon
														id="edit-description"
														sx={{
															ml: "1rem",
															cursor: "pointer",
														}}
														onClick={(event) =>
															handleChangeText(event, "description")
														}
													/>
												</td>
											</tr>
											<tr className="table-row">
												<th>Used Master Contract</th>
												{usedMC ? (
													<td>
														<Typography
															component={Link}
															to={`/library/master-contract/${usedMC._id}`}
															sx={{
																fontFamily: "Montserrat",
															}}
														>
															{usedMC?.customName ?? usedMC.type}
														</Typography>
													</td>
												) : (
													<td>No info</td>
												)}
											</tr>
											{contract?.type === "ERC20" ? (
												<tr className="table-row">
													<th>Max supply</th>
													<td>
														{erc20TokensTotalSupply}{" "}
														{Number(erc20TokensMaxSupply) === 0
															? ""
															: ` / ${erc20TokensMaxSupply}`}
													</td>
												</tr>
											) : (
												<tr className="table-row">
													<th>Max supply</th>
													<td>
														{nftTotalSupply}{" "}
														{Number(nftMaxSupply) === 0
															? ""
															: ` / ${nftMaxSupply}`}
													</td>
												</tr>
											)}
											{contract?.type === "ERC20" ? (
												Number(erc20TokensMaxSupply > 0) ? (
													<tr className="table-row">
														<th>Available to mint</th>
														<td>
															{Number(erc20TokensMaxSupply) -
																Number(erc20TokensTotalSupply)}
														</td>
													</tr>
												) : (
													<></>
												)
											) : Number(nftMaxSupply) > 0 ? (
												<tr className="table-row">
													<th>Available to mint</th>
													<td>
														{Number(nftMaxSupply) - Number(nftTotalSupply)}
													</td>
												</tr>
											) : (
												<></>
											)}
										</tbody>
									</table>
								</div>
							</div>
							<div>
								<table className="mt-2 mt-xl-0 w-100">
									<tbody>
										<tr>
											<th>Used GAS for deployed</th>
											<td>{contractUseddGas ?? <Spinner size="sm" />}</td>
										</tr>
										<tr>
											<th>Used GAS for minting</th>
											{contract?.type === "ERC20" ? (
												<td>{totalMintGasUsed ?? <Spinner size="sm" />}</td>
											) : (
												<td>{tokentUsedGas ?? <Spinner size="sm" />}</td>
											)}
										</tr>
										<tr className="table-row">
											<th></th>
											<th>Total</th>
											<th>Owned</th>
											{contract?.type !== "ERC20" && (
												<>
													<th>Transferred</th>
													<th>Burnt</th>
												</>
											)}
										</tr>
										{["ERC721", "ERC721A", "ERC721PSI"].includes(
											contract?.type
										) ? (
											<tr>
												<th>Tokens ERC721</th>
												<td>
													{tokensAmount ? (
														`${tokensAmount.totalSingle}`
													) : (
														<Spinner size="sm" />
													)}
												</td>
												<td>
													{tokensAmount?.ownedSingle ?? <Spinner size="sm" />}
												</td>
												<td>
													{tokensAmount?.transferredSingle ?? (
														<Spinner size="sm" />
													)}
												</td>
												<td>
													{tokensAmount?.burntSingle ?? <Spinner size="sm" />}
												</td>
											</tr>
										) : contract?.type === "ERC1155" ? (
											<tr>
												<th>Tokens {contract?.type ?? ""}</th>
												<td>
													{tokensAmount ? (
														`${tokensAmount?.totalMulti ?? 0}(${
															tokensAmount?.totalMultiAmount ?? 0
														})`
													) : (
														<Spinner size="sm" />
													)}
												</td>
												<td>
													{tokensAmount ? (
														`${tokensAmount?.ownedMulti ?? 0}(${
															tokensAmount?.ownedMultiAmount ?? 0
														})`
													) : (
														<Spinner size="sm" />
													)}
												</td>
												<td>
													{tokensAmount ? (
														`${tokensAmount?.transferredMultiAmount ?? 0}`
													) : (
														<Spinner size="sm" />
													)}
												</td>
												<td>
													{tokensAmount ? (
														tokensAmount?.burntMulti ?? 0
													) : (
														<Spinner size="sm" />
													)}
												</td>
											</tr>
										) : (
											<tr>
												<th>Tokens {contract?.type ?? ""}</th>
												<td>
													{erc20TokensTotalSupply !== "" ? (
														erc20TokensTotalSupply
													) : (
														<Spinner size="sm" />
													)}
												</td>
												<td>
													{erc20Balance !== "" ? (
														erc20Balance
													) : (
														<Spinner size="sm" />
													)}
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</CardBody>
					</Card>
					<Card className="mb-3">
						<CardBody>
							{contract?.type === "ERC20" ? (
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										alignItems: "space-between",
										justifyContent: "center",
									}}
								>
									{balancesWithHolders.length > 0 ? (
										<>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													margin: "6px 5%",
												}}
											>
												<span style={{ fontWeight: 600 }}>Holder</span>
												<span style={{ fontWeight: 600 }}>Balance</span>
											</div>
											<hr />
											{balancesWithHolders.map((balanceWithHolder) => (
												<>
													<div
														key={balanceWithHolder.holder}
														style={{
															display: "flex",
															justifyContent: "space-between",
															margin: "6px 5%",
														}}
													>
														<span>{balanceWithHolder.holder}</span>
														<span>
															{balanceWithHolder.balance}{" "}
															{contract.symbol ?? ""}
														</span>
													</div>
													<hr />
												</>
											))}
										</>
									) : (
										<span></span>
									)}
								</div>
							) : (
								// <></>
								<AssetsList tokens={tokens} />
							)}
						</CardBody>
					</Card>
				</Box>
				<TokensModal show={show} stage={stage} error={error} action={action} />
			</Box>
			<EditPopup
				anchorEl={anchorEl}
				setAnchorEl={setAnchorEl}
				contractId={contractId}
			/>
		</>
	);
}
